'use strict'
import * as React from 'react'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

/**
 * 対象期間に応じた RANK の参照元（条件式の [参照元.項目] と一致させる）
 *
 * @param {'monthly' | 'quarter' | 'yearly' | '' | undefined} periodKind
 * @returns {{ label: string; value: string }[]}
 */
export const getRankSourceOptions = (periodKind) => {
  if (periodKind === 'monthly') {
    return [
      { label: '今月データ', value: '今月' },
      { label: '前月データ', value: '前月' }
    ]
  }
  if (periodKind === 'quarter') {
    return [
      { label: '今四半期データ', value: '今四半期' },
      { label: '前四半期データ', value: '前四半期' }
    ]
  }
  if (periodKind === 'yearly') {
    return [
      { label: '今年度データ', value: '今年度' },
      { label: '前年度データ', value: '前年度' },
      { label: '今年度末月次データ', value: '今年度末月次' },
      { label: '前年度末月次データ', value: '前年度末月次' }
    ]
  }
  return []
}

/**
 * RANK関数ウィザードの本文（showModal の body に渡すコンテンツ） 右側の項目プルダウンは、サブデータ取得APIの dataClassifications を表示する。
 *
 * @param {Object} props
 * @param {'monthly' | 'quarter' | 'yearly'} props.periodKind 判定結果の設定先「対象期間」（参照元プルダウンの出し分け）
 * @param {() => void} props.onClose 閉じる
 * @param {(text: string) => void} props.onInsert 生成した式を条件式に挿入するコールバック
 * @param {{ DataClassificationCode: string; DataClassificationName: string }[]} [props.dataClassifications] 項目プルダウン用
 */
export const RankFunctionModalContent = ({ periodKind, onClose, onInsert, dataClassifications = [] }) => {
  const classifications = Array.isArray(dataClassifications) ? dataClassifications : []
  const rankSourceOptions = React.useMemo(() => getRankSourceOptions(periodKind), [periodKind])

  const [source, setSource] = React.useState(() => getRankSourceOptions(periodKind)[0]?.value ?? '')
  const [field, setField] = React.useState('')

  React.useEffect(() => {
    const opts = getRankSourceOptions(periodKind)
    setSource((prev) => {
      if (opts.length === 0) return ''
      const valid = opts.some((o) => o.value === prev)
      return valid ? prev : opts[0].value
    })
  }, [periodKind])

  React.useEffect(() => {
    if (classifications.length > 0) {
      setField((prev) => {
        const currentInList = classifications.some((c) => c.DataClassificationName === prev)
        return currentInList ? prev : classifications[0].DataClassificationName
      })
    } else {
      setField('')
    }
  }, [classifications])

  const expression = `RANK([${source}.${field}])`
  const japaneseDescription = `💡 ${source}の${field}を順位（数値）に変換`

  const handleInsert = () => {
    if (!source || !field) return
    onInsert(expression)
    onClose()
  }

  return (
    <div className="max-h-[60vh] overflow-y-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
        <strong className="text-blue-900">この関数について</strong>
        <p className="text-blue-700 text-sm mt-1.5">
          等級コード（S, A, B, C など）を順位の数値に変換します。
          <br />
          これにより等級同士の比較や差分計算が可能になります。
        </p>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-semibold text-gray-700 mb-2">どの項目の順位を取得しますか？</label>
        <div className="flex gap-3 items-center">
          <div className="flex-1">
            <ChrofyCustomSelect
              className="w-full min-w-0"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              hasNoSelect={false}
              options={rankSourceOptions ?? []}
            />
          </div>
          <div className="flex-1">
            <ChrofyCustomSelect
              className="w-full min-w-0"
              name="項目（サブデータAPIのdataClassifications）"
              value={field}
              onChange={(e) => setField(e.target.value)}
              hasNoSelect={false}
              placeholder="項目を選択してください"
              options={[
                { label: '項目を選択してください', value: '' },
                ...(classifications ?? []).map((c) => ({
                  label: c.DataClassificationName,
                  value: c.DataClassificationName
                }))
              ]}
            />
          </div>
        </div>
      </div>

      <div className="bg-slate-800 text-slate-200 p-4 rounded-lg font-mono text-sm mt-5">
        <span className="block text-xs text-slate-400 mb-2">生成される式</span>
        <div>{expression}</div>
        <div className="bg-green-50 text-green-800 p-3.5 rounded-md mt-3.5 font-sans text-sm">
          {japaneseDescription}
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          className="px-5 py-2.5 bg-gray-200 border-none rounded-md text-sm font-medium cursor-pointer hover:bg-gray-300"
          onClick={onClose}
        >
          キャンセル
        </button>
        <button
          type="button"
          className="px-5 py-2.5 bg-[#3150A5] text-white border-none rounded-md text-sm font-medium cursor-pointer hover:bg-[#264089] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleInsert}
          disabled={!field || !source}
        >
          式に挿入
        </button>
      </div>
    </div>
  )
}

export default RankFunctionModalContent
